/**
 * Custom hook for managing engagement data
 * Handles loading from API and state management
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useEngagementApi } from '@/hooks/use-engagement-api'
import { 
  formStateToSavePatchOperations,
  apiToFormState,
  formatDateOnly,
  STUB_RECORD_ID,
} from '@/lib/adapters/engagement-adapter'
import type { FormState } from '@/types/ui/engagement'
import type { EngagementResponse, TransactionHeaderSummary, EngagementReferences } from '@/types/api/engagement'
import { ENGAGEMENT_ACTIONS } from '@/lib/constants/engagement-actions'

interface UseEngagementDataReturn {
  // Data group
  data: {
    recordId: number
    version: number
    formState: FormState | null
    allVersions: EngagementResponse[] | null
    previousWorkerIds: number[] | null
  }
  
  // Capabilities group (what actions are available)
  capabilities: {
    canSave: boolean
    canSubmit: boolean
    canSubmitForReview: boolean
    canApprove: boolean
    canDecline: boolean
    canDiscard: boolean
    canCreateChangeRequest: boolean
    canGenerateMilestoneTransaction: boolean
    canRecall: boolean
    canRecallToAccounting: boolean
    canComplete: boolean
    canReactivate: boolean
    canLockRevenueRecognition: boolean
    canViewWorkerPayRates: boolean
    canEditEngagementSource: boolean
    references: EngagementReferences
  }

  // Handlers group (action functions)
  handlers: {
    refreshData: () => Promise<void>
    saveEngagement: (formState: FormState) => Promise<void>
    submitEngagement: (formStateToSubmit?: FormState) => Promise<void>
    submitEngagementForReview: (formStateToSubmit?: FormState) => Promise<void>
    approveEngagement: () => Promise<void>
    declineEngagement: () => Promise<void>
    discardEngagement: () => Promise<void>
    createChangeRequest: (comment: string) => Promise<{ recordId: number; version: number } | null>
    generateMilestoneTransaction: (milestoneReference: string) => Promise<TransactionHeaderSummary>
    recallEngagement: () => Promise<void>
    recallEngagementToAccounting: () => Promise<void>
    completeEngagement: (completionDate?: Date | null, closedReasonCode?: string | null) => Promise<void>
    reactivateEngagement: () => Promise<void>
  }
  
  // Status group
  status: {
    loading: boolean
    error: string | null
    errorIsAccessOrMissing: boolean
    isHandlingAction: boolean
    currentAction: string
    lastSaved: Date | null
  }
}

export function useEngagementData(requestedRecordId: number = STUB_RECORD_ID, requestedVersion?: number): UseEngagementDataReturn {
  const engagementApi = useEngagementApi()
  
  // Helper to get previous version worker IDs for change request logic
  const getPreviousWorkerIds = useCallback((allVersions: EngagementResponse[], currentVersion: number): number[] => {
    const previousVersionNumber = currentVersion - 1
    
    if (previousVersionNumber < 1) {
      return [] // No previous version
    }
    
    // Find the previous version in the existing allVersions data
    const previousVersion = allVersions.find(v => v.data.version === previousVersionNumber)
    
    if (!previousVersion) {
      return [] // Previous version not found
    }
    
    // Extract worker IDs from the previous version data
    return previousVersion.data.workers?.map(w => w.workerUserProfileId).filter(id => id != null) || []
  }, [])
  
  // Helper method to load engagement using 2-request pattern
  const loadEngagementData = useCallback(async (requestedRecordId: number, requestedVersion?: number): Promise<{ formState: FormState, allVersions: EngagementResponse[] }> => {
    // 1. Get all versions to find the latest one
    const versionsResponse = await engagementApi.getEngagementVersions(requestedRecordId)
    
    if (!versionsResponse.data || versionsResponse.data.length === 0) {
      throw new Error('No engagement versions found')
    }

    // 2. Determine which version to load
    let versionToLoad: number
    if (requestedVersion) {
      // Check if requested version exists
      const requestedVersionExists = versionsResponse.data.some(v => v.data.version === requestedVersion)
      if (!requestedVersionExists) {
        throw new Error(`Version ${requestedVersion} not found`)
      }
      versionToLoad = requestedVersion
    } else {
      // Find the latest version (highest version number)
      const latestVersionResponse = versionsResponse.data.sort((a, b) => b.data.version - a.data.version)[0]
      versionToLoad = latestVersionResponse.data.version
    }

    // 3. Get the specific version with full data and action context
    const versionResponse = await engagementApi.getEngagementVersion(requestedRecordId, versionToLoad)

    // 4. Calculate previous worker IDs for change request logic
    const previousWorkerIds = getPreviousWorkerIds(versionsResponse.data, versionToLoad)
    
    // 5. Transform API response to FormState (includes action context from HATEOAS)
    const formState = apiToFormState(versionResponse, previousWorkerIds)
    
    return { formState, allVersions: versionsResponse.data }
  }, [engagementApi, getPreviousWorkerIds])
  
  // State management
  const [loadedRecordId, setLoadedRecordId] = useState<number>(requestedRecordId)
  const [loadedVersion, setLoadedVersion] = useState<number>(requestedVersion ?? 1)
  const [formState, setFormState] = useState<FormState | null>(null)
  const [allVersions, setAllVersions] = useState<EngagementResponse[] | null>(null)
  const [previousWorkerIds, setPreviousWorkerIds] = useState<number[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [isHandlingAction, setIsHandlingAction] = useState(false)
  const [currentAction, setCurrentAction] = useState<string>("")
  const [error, setError] = useState<string | null>(null)
  const [errorIsAccessOrMissing, setErrorIsAccessOrMissing] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // Load engagement data
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setErrorIsAccessOrMissing(false)

      console.log(`Loading engagement data for recordId: ${requestedRecordId}`)

      const { formState, allVersions } = await loadEngagementData(requestedRecordId, requestedVersion)

      const recordId = formState.actionContext.engagement.recordId
      const version = formState.actionContext.engagement.version
      // Calculate previous version worker IDs for change request logic
      const prevWorkerIds = getPreviousWorkerIds(allVersions, version)

      setLoadedRecordId(recordId)
      setLoadedVersion(version)
      setAllVersions(allVersions)
      setFormState(formState)
      setPreviousWorkerIds(prevWorkerIds)
      setLastSaved(formState.lastModifiedDateTime || null)
      console.log('Engagement data loaded successfully:', formState)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load engagement data'
      // useApi throws plain Error with status embedded in the message ("...status 404"), and the
      // local guards above throw "No engagement versions found" / "Version X not found". Both map
      // to the gated-access copy with no retry; everything else falls through to a generic retry.
      const isAccessOrMissing = /\bnot found\b|\bforbidden\b|access denied|status 40[34]\b/i.test(errorMessage)
      setError(errorMessage)
      setErrorIsAccessOrMissing(isAccessOrMissing)
      console.error('Failed to load engagement:', err)
    } finally {
      setLoading(false)
    }
  }, [requestedRecordId, requestedVersion, loadEngagementData, getPreviousWorkerIds])

  // Save engagement data
  const saveEngagement = useCallback(async (updatedFormState: FormState) => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.SAVE)
      setError(null)
      
      console.log('Saving engagement data:', updatedFormState)
      
      // 1. Transform form state to patch operations
      const patchOperations = formStateToSavePatchOperations(updatedFormState)
      
      // 2. Make API call using engagement API hook
      await engagementApi.saveEngagement(
        loadedRecordId, 
        loadedVersion, 
        patchOperations
      )
      
      console.log('Engagement saved successfully')
      
      // 3. Reload data to get updated lastModifiedDateTime from server
      await loadData()
      
    } catch (err) {
      console.error('Failed to save engagement:', err)
      throw err // Re-throw so caller can handle
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  // Submit engagement for review
  const submitEngagement = useCallback(async (formStateToSubmit?: FormState) => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.SUBMIT)
      setError(null)
      
      // If formStateToSubmit is provided, save it first
      if (formStateToSubmit) {
        console.log('Saving engagement before submission...')
        await saveEngagement(formStateToSubmit)
        console.log('Engagement saved, now submitting...')
      }
      
      console.log('Submitting engagement for review')
      
      // Make API call using engagement API hook
      await engagementApi.submitEngagement(
        loadedRecordId,
        loadedVersion
      )
      
      console.log('Engagement submitted successfully')
      
      // Refresh data to get updated status and available actions
      await loadData()
      
    } catch (err) {
      console.error('Failed to submit engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData, saveEngagement])

  const submitEngagementForReview = useCallback(async (formStateToSubmit?: FormState) => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW)
      setError(null)
      
      // If formStateToSubmit is provided, save it first
      if (formStateToSubmit) {
        console.log('Saving engagement before submitting for review...')
        await saveEngagement(formStateToSubmit)
        console.log('Engagement saved, now submitting for review...')
      }
      
      await engagementApi.submitForReview(loadedRecordId, loadedVersion)
      await loadData()
      
    } catch (err) {
      console.error('Failed to submit for review:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData, saveEngagement])

  // Approve engagement
  const approveEngagement = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.APPROVE)
      setError(null)
      
      console.log('Approving engagement')
      
      // Make API call using engagement API hook
      await engagementApi.approveEngagement(
        loadedRecordId,
        loadedVersion
      )
      
      console.log('Engagement approved successfully')
      
      // Refresh data to get updated status and available actions
      await loadData()
      
    } catch (err) {
      console.error('Failed to approve engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  // Decline engagement
  const declineEngagement = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.DECLINE)
      setError(null)
      
      console.log('Declining engagement')
      
      // Make API call using engagement API hook
      await engagementApi.declineEngagement(
        loadedRecordId,
        loadedVersion
      )
      
      console.log('Engagement declined successfully')
      
      // Refresh data to get updated status and available actions
      await loadData()
      
    } catch (err) {
      console.error('Failed to decline engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  // Create change request
  const createChangeRequest = useCallback(async (comment: string): Promise<{ recordId: number; version: number } | null> => {
    if (!formState) {
      throw new Error('No form state available')
    }

    if (!comment || comment.trim().length === 0) {
      throw new Error('Comment is required to create a change request')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.CREATE_CHANGE_REQUEST)
      setError(null)
      
      console.log('Creating change request')
      
      // Make API call using engagement API hook
      const response = await engagementApi.createChangeRequest(loadedRecordId, comment.trim())
      
      console.log('Change request created successfully')
      
      // Return the new version info for navigation
      return {
        recordId: response.data.recordId,
        version: response.data.version
      }
      
    } catch (err) {
      console.error('Failed to create change request:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, engagementApi])

  // Generate milestone transaction
  const generateMilestoneTransaction = useCallback(async (milestoneReference: string): Promise<TransactionHeaderSummary> => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction("generating_milestone_transaction")
      setError(null)
      
      console.log('Generating milestone transaction for:', milestoneReference)
      
      // Make API call using engagement API hook
      const response = await engagementApi.generateMilestoneTransaction(
        loadedRecordId,
        loadedVersion,
        milestoneReference
      )
      
      console.log('Milestone transaction generated successfully')
      
      // Return the full transaction summary
      return response
      
    } catch (err) {
      console.error('Failed to generate milestone transaction:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi])

  // Discard engagement
  const discardEngagement = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.DISCARD)
      setError(null)
      
      console.log('Discarding engagement')
      
      // Make API call using engagement API hook
      await engagementApi.discardEngagement(
        loadedRecordId,
        loadedVersion
      )
      
      console.log('Engagement discarded successfully')
      
      // Don't refresh data - let the page handle navigation
      // For change requests, the page will redirect to the base engagement
      // For drafts, the page will navigate back to the list
      
    } catch (err) {
      console.error('Failed to discard engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi])

  const recallEngagement = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.RECALL)
      setError(null)
      
      await engagementApi.recallEngagement(loadedRecordId, loadedVersion)
      await loadData()
      
    } catch (err) {
      console.error('Failed to recall engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  const recallEngagementToAccounting = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.RECALL_TO_ACCOUNTING)
      setError(null)
      
      await engagementApi.recallToAccounting(loadedRecordId, loadedVersion)
      await loadData()
      
    } catch (err) {
      console.error('Failed to recall to accounting:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  const completeEngagement = useCallback(async (completionDate?: Date | null, closedReasonCode?: string | null) => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.COMPLETE)
      setError(null)
      
      await engagementApi.completeEngagement(loadedRecordId, loadedVersion, {
        completionDate: completionDate ? formatDateOnly(completionDate) : null,
        closedReasonCode
      })
      await loadData()
      
    } catch (err) {
      console.error('Failed to complete engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  const reactivateEngagement = useCallback(async () => {
    if (!formState) {
      throw new Error('No form state available')
    }

    try {
      setIsHandlingAction(true)
      setCurrentAction(ENGAGEMENT_ACTIONS.REACTIVATE)
      setError(null)
      
      await engagementApi.reactivateEngagement(loadedRecordId, loadedVersion)
      await loadData()
      
    } catch (err) {
      console.error('Failed to reactivate engagement:', err)
      throw err
    } finally {
      setIsHandlingAction(false)
      setCurrentAction("")
    }
  }, [formState, loadedRecordId, loadedVersion, engagementApi, loadData])

  // Refresh data function
  const refreshData = useCallback(async () => {
    await loadData()
  }, [loadData])

  // Load data on mount and when recordId or version changes
  useEffect(() => {
    loadData()
  }, [requestedRecordId, requestedVersion])

  // Memoize handlers object for stable reference
  const handlers = useMemo(() => ({
    refreshData,
    saveEngagement,
    submitEngagement,
    submitEngagementForReview,
    approveEngagement,
    declineEngagement,
    discardEngagement,
    createChangeRequest,
    generateMilestoneTransaction,
    recallEngagement,
    recallEngagementToAccounting,
    completeEngagement,
    reactivateEngagement
  }), [refreshData, saveEngagement, submitEngagement, submitEngagementForReview, approveEngagement, declineEngagement, discardEngagement, createChangeRequest, generateMilestoneTransaction, recallEngagement, recallEngagementToAccounting, completeEngagement, reactivateEngagement])

  // Memoize capabilities object for stable reference
  const capabilities = useMemo(() => ({
    canSave: formState?.actionContext.canSave ?? false,
    canSubmit: formState?.actionContext.canSubmit ?? false,
    canSubmitForReview: formState?.actionContext.canSubmitForReview ?? false,
    canApprove: formState?.actionContext.canApprove ?? false,
    canDecline: formState?.actionContext.canDecline ?? false,
    canDiscard: formState?.actionContext.canDiscard ?? false,
    canCreateChangeRequest: (formState?.actionContext.canCreateChangeRequest && !formState?.actionContext.canSave) ?? false,
    canGenerateMilestoneTransaction: formState?.actionContext.canGenerateMilestoneTransaction ?? false,
    canRecall: formState?.actionContext.canRecall ?? false,
    canRecallToAccounting: formState?.actionContext.canRecallToAccounting ?? false,
    canComplete: formState?.actionContext.canComplete ?? false,
    canReactivate: formState?.actionContext.canReactivate ?? false,
    canLockRevenueRecognition: formState?.actionContext.canLockRevenueRecognition ?? false,
    canViewWorkerPayRates: formState?.actionContext.canViewWorkerPayRates ?? false,
    canEditEngagementSource: formState?.actionContext.canEditEngagementSource ?? false,
    references: formState?.actionContext.references ?? { profiles: {}, organizations: {}, engagements: {} }
  }), [formState?.actionContext])

  return {
    // Data group
    data: {
      recordId: loadedRecordId,
      version: loadedVersion,
      formState,
      allVersions,
      previousWorkerIds
    },
    
    // Capabilities group (what actions are available)
    capabilities,
    
    // Handlers group
    handlers,
    
    // Status group
    status: {
      loading,
      error,
      errorIsAccessOrMissing,
      isHandlingAction,
      currentAction,
      lastSaved
    }
  }
} 